import type { StoredTeamRole } from '../../../hooks/types';
import { objValueNullToUndefined } from '../../../lib/utils';
import type {
  RoleGroupPriority,
  RoleInformation,
  RolePriority,
} from '../types';

/**
 * Returns information for a particular role required to render the
 * filter order and tag colour assignment of a role group
 * (Assigns a colour and a filter priority to every role within the given input)
 */
export const getRoleInformation = (
  teamRoles: readonly StoredTeamRole[]
): readonly RoleInformation[] =>
  teamRoles.map((role) => {
    const derivedRole = objValueNullToUndefined<StoredTeamRole>(role);

    const roleGroup = derivedRole?.role_group ?? 'Other';

    const otherGroupFontColour = '#000000';

    const priority = roleGroupPriorities.find((p) => p.roleGroup === roleGroup);

    return {
      roleGroup,
      name: role.name,
      teamRoleId: role.id,
      colour: priority?.colour ?? '#ffffff',
      textColour:
        roleGroup === 'Other' ? otherGroupFontColour : priority?.textColour,
      roleGroupPriority: priority?.roleGroupPriority ?? 0,
    };
  });

/**
 * These colours have been eyedrop-colour-picked from the RFS org chart.
 * When rendered in a tag, the default textColour is white.
 */
export const colours = {
  black: { colour: '#000000' },
  grey: { colour: '#9C9C9C', textColour: '#000000' },
  yellowgreen: { colour: '#C4D712', textColour: '#000000' },
  lightgreen: { colour: `#99CC01`, textColour: '#000000' },
  darkblue: { colour: '#1212FF' },
  yellow: { colour: '#F9ED22', textColour: '#000000' },
  silver: { colour: '#C0CFE2', textColour: '#000000' },
  red: { colour: '#FF0000' },
  // Not in the org chart
  lightgrey: { colour: '#BABABA', textColour: '#000000' },
};
/**
 * The following colours are automatically, nicely styled by AntD in tags.
 * To use more colours, the tags must be custom-rendered.
 * 'magenta', 'red', 'volcano', 'orange', 'gold', 'lime', 'green', 'cyan', 'blue', 'geekblue', 'purple'
 */
export const roleGroupPriorities: readonly RoleGroupPriority[] = [
  {
    roleGroup: 'SOC',
    roleGroupPriority: 10000,
    ...colours.black,
  },
  {
    roleGroup: 'Control',
    roleGroupPriority: 20000,
    ...colours.black,
  },
  {
    roleGroup: 'Management',
    roleGroupPriority: 25000,
    ...colours.grey,
  },
  {
    roleGroup: 'Investigation',
    roleGroupPriority: 30000,
    ...colours.lightgreen,
  },
  {
    roleGroup: 'Rural',
    roleGroupPriority: 35000,
    ...colours.black,
  },
  {
    roleGroup: 'Public Information',
    roleGroupPriority: 40000,
    ...colours.yellowgreen,
  },
  {
    roleGroup: 'Logistics',
    roleGroupPriority: 50000,
    ...colours.darkblue,
  },
  {
    roleGroup: 'Finance',
    roleGroupPriority: 60000,
    ...colours.silver,
  },
  {
    roleGroup: 'Intelligence',
    roleGroupPriority: 70000,
    ...colours.yellow,
  },
  {
    roleGroup: 'Planning',
    roleGroupPriority: 80000,
    ...colours.yellow,
  },
  {
    roleGroup: 'Operations',
    roleGroupPriority: 90000,
    ...colours.red,
  },
  {
    roleGroup: 'Operations (Air Ops)',
    roleGroupPriority: 100000,
    ...colours.red,
  },
  {
    roleGroup: 'Operations (Field Ops)',
    roleGroupPriority: 110000,
    ...colours.red,
  },
  {
    roleGroup: 'Agency Liaison',
    roleGroupPriority: 120000,
    ...colours.black,
  },
  {
    roleGroup: 'Emergency Operations Centre',
    roleGroupPriority: 130000,
    ...colours.lightgrey,
  },
];

/**
 * Calculates the IMT role's sorting priority based on role group and role's sub priority within a role group.
 * @param roleGroupPriority role group's priority.
 * @param roleSubPriority the role's priority within a role group.
 * @returns sorting priority number.
 */
export const roleSortPriority = (
  roleGroupPriority: number,
  roleSubPriority: number
) => roleGroupPriority + 10 * roleSubPriority;

/**
 * Default role's sub priority for the custom roles that are not part of curated roles.
 * As the gap between role groups is 10000, and we multiple role's sub priority by 10 when calculating the total priority,
 * the range for role sub priority should be between 0 and 999.
 * Use high number to display roles that are not prioritized at the end of the list.
 */
export const defaultRoleSubPriority = 999;

// These are initially separated 5 apart, leaving room for new roles to be added in between.
const roles: readonly RolePriority[] = [
  { role: 'Commissioner', subPriority: 1, roleGroup: 'SOC' },
  {
    role: 'Deputy Commissioner Field Operations',
    subPriority: 5,
    roleGroup: 'SOC',
  },
  { role: 'Director State Operations', subPriority: 10, roleGroup: 'SOC' },
  { role: 'State Operations Controller', subPriority: 15, roleGroup: 'SOC' },
  {
    role: 'Deputy State Operations Controller',
    subPriority: 20,
    roleGroup: 'SOC',
  },
  { role: 'State Duty Operations Officer', subPriority: 25, roleGroup: 'SOC' },
  { role: 'Management Support', subPriority: 30, roleGroup: 'SOC' },
  {
    role: 'MIC 1 - Major Incident Coordinator',
    subPriority: 35,
    roleGroup: 'SOC',
  },
  { role: 'MIC 1 - Intelligence Officer', subPriority: 40, roleGroup: 'SOC' },
  { role: 'MIC 1 - Situation Officer', subPriority: 45, roleGroup: 'SOC' },
  { role: 'MIC 1 - Resources Officer', subPriority: 50, roleGroup: 'SOC' },
  { role: 'MIC 1 - Management Support', subPriority: 55, roleGroup: 'SOC' },
  {
    role: 'MIC 2 - Major Incident Coordinator',
    subPriority: 60,
    roleGroup: 'SOC',
  },
  { role: 'MIC 2 - Intelligence Officer', subPriority: 65, roleGroup: 'SOC' },
  { role: 'MIC 2 - Situation Officer', subPriority: 70, roleGroup: 'SOC' },
  { role: 'MIC 2 - Resources Officer', subPriority: 75, roleGroup: 'SOC' },
  { role: 'MIC 2 - Management Support', subPriority: 80, roleGroup: 'SOC' },
  {
    role: 'MIC 3 - Major Incident Coordinator',
    subPriority: 85,
    roleGroup: 'SOC',
  },
  { role: 'MIC 3 - Intelligence Officer', subPriority: 90, roleGroup: 'SOC' },
  { role: 'MIC 3 - Situation Officer', subPriority: 95, roleGroup: 'SOC' },
  { role: 'MIC 3 - Resources Officer', subPriority: 100, roleGroup: 'SOC' },
  { role: 'MIC 3 - Management Support', subPriority: 105, roleGroup: 'SOC' },
  {
    role: 'MIC 4 - Major Incident Coordinator',
    subPriority: 110,
    roleGroup: 'SOC',
  },
  { role: 'MIC 4 - Intelligence Officer', subPriority: 115, roleGroup: 'SOC' },
  { role: 'MIC 4 - Situation Officer', subPriority: 120, roleGroup: 'SOC' },
  { role: 'MIC 4 - Resources Officer', subPriority: 125, roleGroup: 'SOC' },
  { role: 'MIC 4 - Management Support', subPriority: 130, roleGroup: 'SOC' },
  { role: 'ILU Coordinator', subPriority: 131, roleGroup: 'SOC' },
  { role: 'ILU Deputy Coordinator', subPriority: 132, roleGroup: 'SOC' },
  { role: 'ILU Support', subPriority: 133, roleGroup: 'SOC' },
  { role: 'State Intelligence Officer', subPriority: 135, roleGroup: 'SOC' },
  { role: 'State Resource Officer', subPriority: 140, roleGroup: 'SOC' },
  { role: 'State Air Operations Manager', subPriority: 145, roleGroup: 'SOC' },
  { role: 'State Air Operations Officer', subPriority: 150, roleGroup: 'SOC' },
  { role: 'Aviation NPWS Liaison Officer', subPriority: 155, roleGroup: 'SOC' },
  { role: 'RART Coordinator', subPriority: 160, roleGroup: 'SOC' },
  { role: 'RART Dispatcher', subPriority: 165, roleGroup: 'SOC' },
  {
    role: 'Public Information Coordinator',
    subPriority: 170,
    roleGroup: 'SOC',
  },
  {
    role: 'State Public Information Officer',
    subPriority: 175,
    roleGroup: 'SOC',
  },
  {
    role: 'State Public Information Assistant',
    subPriority: 176,
    roleGroup: 'SOC',
  },
  { role: 'State Media Supervisor', subPriority: 180, roleGroup: 'SOC' },
  { role: 'State Media Assistant', subPriority: 181, roleGroup: 'SOC' },
  { role: 'State Duty Media Officer', subPriority: 185, roleGroup: 'SOC' },
  { role: 'Media Officer', subPriority: 190, roleGroup: 'SOC' },
  { role: 'Online Content Supervisor', subPriority: 191, roleGroup: 'SOC' },
  { role: 'State Website Officer', subPriority: 195, roleGroup: 'SOC' },
  { role: 'Social Media Officer', subPriority: 200, roleGroup: 'SOC' },
  {
    role: 'Bush Fire Information Line Coordinator',
    subPriority: 205,
    roleGroup: 'SOC',
  },
  {
    role: 'Bush Fire Information Line Team Leader',
    subPriority: 210,
    roleGroup: 'SOC',
  },
  { role: 'State Logistics Officer', subPriority: 215, roleGroup: 'SOC' },
  { role: 'State Logistics Duty Officer', subPriority: 220, roleGroup: 'SOC' },
  {
    role: 'State Logistics Support Officer',
    subPriority: 225,
    roleGroup: 'SOC',
  },
  {
    role: 'State Finance Officer',
    subPriority: 227,
    roleGroup: 'SOC',
  },
  { role: 'Senior Fire Behaviour Analyst', subPriority: 230, roleGroup: 'SOC' },
  {
    role: 'Bush Fire Incident Analysis Officer',
    subPriority: 235,
    roleGroup: 'SOC',
  },
  {
    role: 'Bureau of Meteorology Liaison Officer',
    subPriority: 240,
    roleGroup: 'SOC',
  },
  {
    role: 'Operational Tech and Comms Liaison Officer',
    subPriority: 242,
    roleGroup: 'SOC',
  },
  {
    role: 'ICT Service Desk Officer',
    subPriority: 243,
    roleGroup: 'SOC',
  },
  {
    role: 'State GIS Duty Officer',
    subPriority: 245,
    roleGroup: 'SOC',
  },
  {
    role: 'State GIS On-Call Officer',
    subPriority: 246,
    roleGroup: 'SOC',
  },
  {
    role: 'State GIS Field Officer',
    subPriority: 247,
    roleGroup: 'SOC',
  },
  { role: 'Ground Support Coordinator', subPriority: 250, roleGroup: 'SOC' },
  { role: 'BIA State Coordinator', subPriority: 252, roleGroup: 'SOC' },
  {
    role: 'BIA Management Support Officer',
    subPriority: 253,
    roleGroup: 'SOC',
  },
  { role: 'OCC Supervisor', subPriority: 255, roleGroup: 'SOC' },
  { role: 'Emergency Alert Operator', subPriority: 260, roleGroup: 'SOC' },
  { role: 'PRAS Admin', subPriority: 265, roleGroup: 'SOC' },
] as const;

// These roles were the original set, which were not present in the most recent RFS feedback.
// IMT role's priority. This will be combined with role group priority for any display purpose.
const oldRolePriorities: readonly RolePriority[] = [
  {
    role: 'Marine Rescue NSW Liaison',
    subPriority: 2,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'NSWPF Liaison',
    subPriority: 3,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'Environmental Liaison',
    subPriority: 4,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'Australian Defence Force Liaison',
    subPriority: 5,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'Health Liaison',
    subPriority: 6,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'Education Liaison',
    subPriority: 7,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'Transport Liaison',
    subPriority: 8,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'Energy & Utilities Liaison',
    subPriority: 9,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'Telecommunications Liaison',
    subPriority: 10,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'Local Emergency Management Officer',
    subPriority: 11,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'NPWS Liaison',
    subPriority: 12,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'FCNSW Liaison',
    subPriority: 13,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'NSWA Liaison',
    subPriority: 14,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'Surf Lifesaving NSW Liaison ',
    subPriority: 15,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'VRA Rescue NSW Liaison',
    subPriority: 16,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'FRNSW Liaison',
    subPriority: 17,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'NSW SES Liaison',
    subPriority: 18,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'Animal & Agriculture Liaison',
    subPriority: 19,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'Welfare Liaison',
    subPriority: 20,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'Engineering Liaison',
    subPriority: 21,
    roleGroup: 'Agency Liaison',
  },
  {
    role: 'Management Support Officer',
    subPriority: 22,
    roleGroup: 'Control',
  },
  {
    role: 'Incident Controller',
    subPriority: 23,
    roleGroup: 'Control',
  },
  {
    role: 'Control - Scribe',
    subPriority: 24,
    roleGroup: 'Control',
  },
  {
    role: 'Safety Officer',
    subPriority: 25,
    roleGroup: 'Control',
  },
  {
    role: 'Field Safety Officer',
    subPriority: 26,
    roleGroup: 'Control',
  },
  {
    role: 'Critical Incident Support Services',
    subPriority: 27,
    roleGroup: 'Control',
  },
  {
    role: 'Chaplain',
    subPriority: 28,
    roleGroup: 'Control',
  },
  {
    role: 'Deputy Incident Controller',
    subPriority: 29,
    roleGroup: 'Control',
  },
  {
    role: 'EOC - NSW RFS Liaison',
    subPriority: 30,
    roleGroup: 'Emergency Operations Centre',
  },
  {
    role: 'EOC - Scribe',
    subPriority: 31,
    roleGroup: 'Emergency Operations Centre',
  },
  {
    role: 'EOC - Management Support Officer',
    subPriority: 32,
    roleGroup: 'Emergency Operations Centre',
  },
  {
    role: 'Time Keeping',
    subPriority: 33,
    roleGroup: 'Finance',
  },
  {
    role: 'Finance Officer',
    subPriority: 34,
    roleGroup: 'Finance',
  },
  {
    role: 'Accounts',
    subPriority: 35,
    roleGroup: 'Finance',
  },
  {
    role: 'Financial Monitoring',
    subPriority: 36,
    roleGroup: 'Finance',
  },
  {
    role: 'Finance - Scribe',
    subPriority: 37,
    roleGroup: 'Finance',
  },
  {
    role: 'Compensation & Insurance',
    subPriority: 38,
    roleGroup: 'Finance',
  },
  {
    role: 'Modelling & Predictions',
    subPriority: 39,
    roleGroup: 'Intelligence',
  },
  {
    role: 'Intelligence Officer',
    subPriority: 40,
    roleGroup: 'Intelligence',
  },
  {
    role: 'Fire Behaviour Analyst',
    subPriority: 41,
    roleGroup: 'Intelligence',
  },
  {
    role: 'Situation',
    subPriority: 42,
    roleGroup: 'Intelligence',
  },
  {
    role: 'Mapping',
    subPriority: 43,
    roleGroup: 'Intelligence',
  },
  {
    role: 'Technical Advice',
    subPriority: 44,
    roleGroup: 'Intelligence',
  },
  {
    role: 'Intelligence - Scribe',
    subPriority: 45,
    roleGroup: 'Intelligence',
  },
  {
    role: 'Bushfire Impact Analysis',
    subPriority: 46,
    roleGroup: 'Investigation',
  },
  {
    role: 'Fire Investigation',
    subPriority: 47,
    roleGroup: 'Investigation',
  },
  {
    role: 'Investigations Officer',
    subPriority: 48,
    roleGroup: 'Investigation',
  },
  {
    role: 'Investigation - Scribe',
    subPriority: 49,
    roleGroup: 'Investigation',
  },
  {
    role: 'Logistics - Scribe',
    subPriority: 50,
    roleGroup: 'Logistics',
  },
  {
    role: 'Communications Support',
    subPriority: 51,
    roleGroup: 'Logistics',
  },
  {
    role: 'Catering',
    subPriority: 52,
    roleGroup: 'Logistics',
  },
  {
    role: 'Facilities',
    subPriority: 53,
    roleGroup: 'Logistics',
  },
  {
    role: 'Ground Support',
    subPriority: 54,
    roleGroup: 'Logistics',
  },
  {
    role: 'Supply',
    subPriority: 55,
    roleGroup: 'Logistics',
  },
  {
    role: 'Logistics Support',
    subPriority: 56,
    roleGroup: 'Logistics',
  },
  {
    role: 'Logistics Officer',
    subPriority: 57,
    roleGroup: 'Logistics',
  },
  {
    role: 'Base Camp Manager',
    subPriority: 58,
    roleGroup: 'Logistics',
  },
  {
    role: 'Base Camp Coordinator',
    subPriority: 59,
    roleGroup: 'Logistics',
  },
  {
    role: 'Medical Services',
    subPriority: 60,
    roleGroup: 'Logistics',
  },
  {
    role: 'District On Call Officer',
    subPriority: 61,
    roleGroup: 'Operations',
  },
  {
    role: 'District Duty Operations Officer',
    subPriority: 62,
    roleGroup: 'Operations',
  },
  {
    role: 'Field Operations Assistant / Scribe',
    subPriority: 63,
    roleGroup: 'Operations',
  },
  {
    role: 'Operations - Scribe',
    subPriority: 64,
    roleGroup: 'Operations',
  },
  {
    role: 'Communications Centre Assistant',
    subPriority: 65,
    roleGroup: 'Operations',
  },
  {
    role: 'Communications Centre Operator',
    subPriority: 66,
    roleGroup: 'Operations',
  },
  {
    role: 'Communications Centre Supervisor',
    subPriority: 67,
    roleGroup: 'Operations',
  },
  {
    role: 'Deputy Operations Officer',
    subPriority: 68,
    roleGroup: 'Operations',
  },
  {
    role: 'Operations Officer',
    subPriority: 69,
    roleGroup: 'Operations',
  },
  {
    role: 'Staging Area Coordinator',
    subPriority: 70,
    roleGroup: 'Operations',
  },
  {
    role: 'Staging Area Manager',
    subPriority: 71,
    roleGroup: 'Operations',
  },
  {
    role: 'Air Observer',
    subPriority: 72,
    roleGroup: 'Operations (Air Ops)',
  },
  {
    role: 'Air Base Manager',
    subPriority: 73,
    roleGroup: 'Operations (Air Ops)',
  },
  {
    role: 'Air Attack Supervisor',
    subPriority: 74,
    roleGroup: 'Operations (Air Ops)',
  },
  {
    role: 'Aircraft Officer',
    subPriority: 75,
    roleGroup: 'Operations (Air Ops)',
  },
  {
    role: 'Air Operations Manager',
    subPriority: 76,
    roleGroup: 'Operations (Air Ops)',
  },
  {
    role: 'Incendiary Operations Supervisor',
    subPriority: 77,
    roleGroup: 'Operations (Air Ops)',
  },
  {
    role: 'Sector Commander',
    subPriority: 78,
    roleGroup: 'Operations (Field Ops)',
  },
  {
    role: 'Divisional Commander',
    subPriority: 79,
    roleGroup: 'Operations (Field Ops)',
  },
  {
    role: 'Remote Area Liaison',
    subPriority: 80,
    roleGroup: 'Operations (Field Ops)',
  },
  {
    role: 'Task Force Leader',
    subPriority: 81,
    roleGroup: 'Operations (Field Ops)',
  },
  {
    role: 'Strike Team Leader',
    subPriority: 82,
    roleGroup: 'Operations (Field Ops)',
  },
  {
    role: 'Response Team Coordinator',
    subPriority: 83,
    roleGroup: 'Operations (Field Ops)',
  },
  {
    role: 'Plant Operations Manager',
    subPriority: 84,
    roleGroup: 'Operations (Field Ops)',
  },
  {
    role: 'Heavy Plant Supervisor',
    subPriority: 85,
    roleGroup: 'Operations (Field Ops)',
  },
  {
    role: 'Resources',
    subPriority: 86,
    roleGroup: 'Planning',
  },
  {
    role: 'Plans',
    subPriority: 87,
    roleGroup: 'Planning',
  },
  {
    role: 'Planning Officer',
    subPriority: 88,
    roleGroup: 'Planning',
  },
  {
    role: 'Planning - Scribe',
    subPriority: 89,
    roleGroup: 'Planning',
  },
  {
    role: 'Planning Communications',
    subPriority: 90,
    roleGroup: 'Planning',
  },
  {
    role: 'Rostering',
    subPriority: 91,
    roleGroup: 'Planning',
  },
  {
    role: 'Media Escort',
    subPriority: 92,
    roleGroup: 'Public Information',
  },
  {
    role: 'Community Field Liaison',
    subPriority: 93,
    roleGroup: 'Public Information',
  },
  {
    role: 'Information & Warnings',
    subPriority: 94,
    roleGroup: 'Public Information',
  },
  {
    role: 'Community Liaison Officer',
    subPriority: 95,
    roleGroup: 'Public Information',
  },
  {
    role: 'Public Information - Scribe',
    subPriority: 96,
    roleGroup: 'Public Information',
  },
  {
    role: 'Media Liaison Officer',
    subPriority: 97,
    roleGroup: 'Public Information',
  },
  {
    role: 'Fireground Media Liaison',
    subPriority: 98,
    roleGroup: 'Public Information',
  },
  {
    role: 'Public Information Officer',
    subPriority: 99,
    roleGroup: 'Public Information',
  },
  {
    role: 'Rural Liaison Officer',
    subPriority: 100,
    roleGroup: 'Public Information',
  },
  {
    role: 'Director Operational Services',
    subPriority: 163,
    roleGroup: 'SOC',
  },
  {
    role: 'Deputy State Operational Controller',
    subPriority: 164,
    roleGroup: 'SOC',
  },
  {
    role: 'RART Coordinator / Dispatcher',
    subPriority: 165,
    roleGroup: 'SOC',
  },
  {
    role: 'Operations Customer Services Centre (Senior)',
    subPriority: 166,
    roleGroup: 'SOC',
  },
  {
    role: 'Strategic Overview Officer',
    subPriority: 167,
    roleGroup: 'SOC',
  },
  {
    role: 'SOC Intelligence Officer',
    subPriority: 168,
    roleGroup: 'SOC',
  },
  {
    role: 'Functional Area - Welfare',
    subPriority: 169,
    roleGroup: 'SOC',
  },
  {
    role: 'Functional Area - Animal & Agriculture Services',
    subPriority: 170,
    roleGroup: 'SOC',
  },
  {
    role: 'Functional Area - Engineering Services',
    subPriority: 171,
    roleGroup: 'SOC',
  },
  {
    role: 'Functional Area - Transport Services',
    subPriority: 172,
    roleGroup: 'SOC',
  },
  {
    role: 'Functional Area - Energy & Utility Services',
    subPriority: 173,
    roleGroup: 'SOC',
  },
  {
    role: 'Functional Area - Telco',
    subPriority: 174,
    roleGroup: 'SOC',
  },
  {
    role: 'Functional Area - Education',
    subPriority: 175,
    roleGroup: 'SOC',
  },
  {
    role: 'Fire and Rescue NSW',
    subPriority: 176,
    roleGroup: 'SOC',
  },
  {
    role: 'National Parks and Wildlife Service',
    subPriority: 177,
    roleGroup: 'SOC',
  },
  {
    role: 'Forestry Corporation NSW',
    subPriority: 178,
    roleGroup: 'SOC',
  },
  {
    role: 'State Emergency Operations Centre',
    subPriority: 179,
    roleGroup: 'SOC',
  },
  {
    role: 'Police',
    subPriority: 180,
    roleGroup: 'SOC',
  },
  {
    role: 'Ambulance',
    subPriority: 181,
    roleGroup: 'SOC',
  },
  {
    role: 'State Emergency Service',
    subPriority: 182,
    roleGroup: 'SOC',
  },
  {
    role: 'Defence',
    subPriority: 183,
    roleGroup: 'SOC',
  },
];

const maxPriorityInUpperSet = Math.max(
  ...roles.map(({ subPriority }) => subPriority)
);

export const rolePriorities: readonly RolePriority[] = roles.concat(
  oldRolePriorities.map((role) => ({
    ...role,
    subPriority: role.subPriority + maxPriorityInUpperSet,
  }))
);
